import { useEffect, useState } from "react";
import {
  LineChart as RLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { api } from "../../api";
import MgmtLayout from "../MgmtLayout";

const cardCls = "bg-bone border border-ink/10 rounded-sm p-6";

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const fmt = (v) =>
  `RM ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Return the first and last calendar date for a `YYYY-MM` string, both as
// ISO YYYY-MM-DD. Used to scope the POS History call to the selected month.
function monthRange(monthStr) {
  const [y, m] = monthStr.split("-").map(Number);
  const first = `${monthStr}-01`;
  const last = new Date(y, m, 0);
  const lastIso = `${monthStr}-${String(last.getDate()).padStart(2, "0")}`;
  return [first, lastIso];
}

export default function MgmtDashboard() {
  const [date, setDate] = useState(todayISO());
  const [month, setMonth] = useState(thisMonth());
  const [closing, setClosing] = useState(null);
  const [chart, setChart] = useState(null);
  const [aov, setAov] = useState(null);
  // POS History summary for the selected month — mirrors the strip inside
  // Sales & Orders → Import from POS → History so the admin sees the same
  // revenue / transactions / AOV / peak hour / channel split up front.
  const [posMonth, setPosMonth] = useState(null);
  // P&L "clear system" — Daily strip + Monthly stack + Payback period.
  const [plDaily, setPlDaily] = useState(null);
  const [plMonthly, setPlMonthly] = useState(null);

  const load = async () => {
    const [start, end] = monthRange(month);
    const [d, c, a, p, pd, pm] = await Promise.all([
      api.get(`/mgmt/reports/daily?date=${date}`).then((r) => r.data),
      api.get(`/mgmt/reports/monthly_chart?month=${month}`).then((r) => r.data),
      api.get(`/mgmt/reports/customer_value?date=${date}&month=${month}`).then((r) => r.data),
      api.get(`/mgmt/sales/import-pos/history?date_from=${start}&date_to=${end}`)
        .then((r) => r.data)
        .catch(() => null),
      api.get(`/mgmt/reports/pl-daily?date=${date}`).then((r) => r.data).catch(() => null),
      api.get(`/mgmt/reports/pl-monthly?month=${month}`).then((r) => r.data).catch(() => null),
    ]);
    setClosing(d);
    setChart(c);
    setAov(a);
    setPosMonth(p);
    setPlDaily(pd);
    setPlMonthly(pm);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, month]);

  return (
    <MgmtLayout title="Dashboard" subtitle="— Today at a glance">
      {/* Date pickers */}
      <div className="flex flex-wrap items-center gap-3 mb-8">
        <label className="overline text-ink-soft">Closing date</label>
        <input
          data-testid="mgmt-date-picker"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="bg-bone border border-ink/15 rounded-sm px-3 py-2 text-sm"
        />
        <label className="overline text-ink-soft ml-6">Chart month</label>
        <input
          data-testid="mgmt-month-picker"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="bg-bone border border-ink/15 rounded-sm px-3 py-2 text-sm"
        />
      </div>

      {/* POS history — monthly summary strip. Mirrors Sales & Orders →
          Import from POS → History so the admin sees the same numbers here. */}
      {posMonth && posMonth.totals && posMonth.totals.days > 0 && (
        <>
          <p className="overline text-matcha-dark mb-3">POS · {month} — {posMonth.totals.days} day{posMonth.totals.days === 1 ? "" : "s"} synced</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="mgmt-pos-month-strip">
            <PosStat
              label="Revenue"
              value={fmt(posMonth.totals.revenue)}
              sub={`${posMonth.totals.days} day${posMonth.totals.days === 1 ? "" : "s"} synced`}
              testid="mgmt-pos-revenue"
            />
            <PosStat
              label="Transactions"
              value={String(posMonth.totals.orders)}
              sub={posMonth.totals.days ? `${(posMonth.totals.orders / posMonth.totals.days).toFixed(1)} / day avg` : "—"}
              testid="mgmt-pos-transactions"
            />
            <PosStat
              label="AOV"
              value={fmt(posMonth.totals.aov)}
              sub="per transaction"
              highlight
              testid="mgmt-pos-aov"
            />
            <PosStat
              label="Peak hour"
              value={posMonth.totals.peak_hour !== null && posMonth.totals.peak_hour !== undefined
                ? `${String(posMonth.totals.peak_hour).padStart(2, "0")}:00`
                : "—"}
              sub="busiest of month"
              testid="mgmt-pos-peak-hour"
            />
            <PosStat
              label="Dine-in / Take-away"
              value={`${posMonth.channels?.dine_in?.transactions || 0} / ${posMonth.channels?.take_away?.transactions || 0}`}
              sub={`RM ${Number(posMonth.channels?.dine_in?.amount || 0).toFixed(0)} / RM ${Number(posMonth.channels?.take_away?.amount || 0).toFixed(0)}`}
              testid="mgmt-pos-channels"
            />
          </div>
        </>
      )}

      {/* -----------------------------------------------------------------
          P&L — the clear system. Daily strip → Monthly stack → Payback.
          Every number below is live from the DB; edit the Outlet
          Investment inline to update the Payback period on the right.
          ----------------------------------------------------------------- */}
      {plDaily && plMonthly && (
        <PLSection date={date} month={month} plDaily={plDaily} plMonthly={plMonthly} onSaved={load} />
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-10">
        {[
          { k: "Revenue", v: closing && fmt(closing.revenue), testid: "mgmt-kpi-revenue" },
          { k: "Orders", v: closing && closing.orders, testid: "mgmt-kpi-orders" },
          { k: "Avg Spend", v: closing && fmt(closing.average_spend), testid: "mgmt-kpi-avg-spend" },
          { k: "Gross Profit", v: closing && fmt(closing.gross_profit), testid: "mgmt-kpi-gross-profit" },
          { k: "Net Profit", v: closing && fmt(closing.net_profit), testid: "mgmt-kpi-net-profit", positive: closing && Number(closing.net_profit) >= 0 },
          // Dual-metric Card scorecard — stacks Variance + Effective MDR with
          // equal prominence, both sourced from the acquirer merchant PDF.
          (() => {
            const card = plMonthly?.commissions?.Card;
            const hasStmt = card?.source === "merchant_stmt";
            const varianceNum = hasStmt ? Number(card.variance || 0) : null;
            const mdrPct = hasStmt ? ((card.rate || 0) * 100) : null;
            return {
              k: `Card · ${month}`,
              testid: "mgmt-kpi-card-mdr",
              dual: hasStmt
                ? [
                    {
                      label: "Variance",
                      value: `RM ${varianceNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                      testid: "mgmt-kpi-card-variance",
                      tone: varianceNum < 0 ? "neg" : varianceNum > 0 ? "pos" : "neutral",
                    },
                    {
                      label: "Effective MDR",
                      value: `${mdrPct.toFixed(3)}%`,
                      testid: "mgmt-kpi-card-mdr-pct",
                      tone: "neutral",
                    },
                  ]
                : null,
              fallback: "Upload acquirer PDF",
            };
          })(),
        ].map((c) => (
          <div key={c.k} className={cardCls} data-testid={c.testid}>
            <p className="overline text-ink-soft">{c.k}</p>
            {c.dual ? (
              <div className="mt-2 space-y-2">
                {c.dual.map((m) => (
                  <div key={m.label} data-testid={m.testid}>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-ink-soft">{m.label}</p>
                    <p
                      className={`font-display text-2xl leading-tight ${
                        m.tone === "neg" ? "text-red-700" : m.tone === "pos" ? "text-emerald-700" : "text-ink"
                      }`}
                      style={{ letterSpacing: "0.02em" }}
                    >
                      {m.value}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <p
                  className={`font-display text-3xl mt-2 ${c.positive === false ? "text-red-700" : "text-ink"}`}
                  style={{ letterSpacing: "0.02em" }}
                >
                  {c.v ?? "—"}
                </p>
                {c.sub && (
                  <p className="text-[10px] uppercase tracking-[0.14em] text-ink-soft mt-1">{c.sub}</p>
                )}
                {c.fallback && c.v === undefined && (
                  <p className="text-[10px] uppercase tracking-[0.14em] text-ink-soft mt-3">{c.fallback}</p>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className={cardCls + " mb-10"} data-testid="mgmt-monthly-chart">
        <div className="flex items-center justify-between mb-6">
          <p className="overline text-matcha-dark">Daily sales · {month}</p>
          {chart && (
            <p className="overline text-ink-soft">
              Total: <span className="text-matcha-dark">{fmt(chart.days.reduce((s, x) => s + x.revenue, 0))}</span>
            </p>
          )}
        </div>
        {!chart ? <p className="text-ink-soft text-sm">Loading…</p> : (
          <div style={{ width: "100%", height: 280 }} data-testid="mgmt-revenue-line">
            <ResponsiveContainer width="100%" height="100%">
              <RLineChart data={chart.days} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid stroke="rgba(20,20,20,0.06)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#7a7a72", fontSize: 11 }}
                  tickFormatter={(d) => (d ? d.slice(8, 10) : "")}
                  axisLine={{ stroke: "rgba(20,20,20,0.15)" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#7a7a72", fontSize: 11 }}
                  axisLine={{ stroke: "rgba(20,20,20,0.15)" }}
                  tickLine={false}
                  tickFormatter={(v) => `RM ${v}`}
                  width={70}
                />
                <Tooltip
                  formatter={(v) => fmt(v)}
                  labelFormatter={(l) => `Date: ${l}`}
                  contentStyle={{ background: "#f6f1e7", border: "1px solid rgba(20,20,20,0.1)", borderRadius: 2, fontSize: 12 }}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#2f4a2a"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#2f4a2a", stroke: "#2f4a2a" }}
                  activeDot={{ r: 6, fill: "#2f4a2a" }}
                  isAnimationActive={false}
                />
              </RLineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Customer Value Analysis — Average Order Value / spend per customer */}
      {aov && <CustomerValueCard aov={aov} />}

      {/* Daily closing detail */}
      {closing && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className={cardCls + " lg:col-span-1"} data-testid="mgmt-daily-closing">
            <p className="overline text-matcha-dark mb-4">Daily Closing · {closing.date}</p>
            <ul className="space-y-3 text-sm">
              {[
                ["Revenue", fmt(closing.revenue)],
                ["Orders", closing.orders],
                ["Avg Spend", fmt(closing.average_spend)],
                ["COGS", fmt(closing.cogs)],
                ["Gross Profit", fmt(closing.gross_profit)],
                ["Food Cost %", `${closing.food_cost_pct}%`],
                ["Labour Cost", fmt(closing.labour_cost)],
                ["Labour Cost %", `${closing.labour_cost_pct ?? 0}%`],
                ["Operating Cost", fmt(closing.operating_cost)],
                ["Operating Cost %", `${closing.operating_cost_pct ?? 0}%`],
                ["Net Profit", fmt(closing.net_profit), Number(closing.net_profit) >= 0 ? "text-matcha-dark" : "text-red-700"],
                ["Best Product", closing.best_selling_product || "—"],
                ["Best Category", closing.best_selling_category || "—"],
                ["Lowest Margin", closing.lowest_margin_product || "—"],
              ].map(([k, v, cls]) => (
                <li key={k} className="flex justify-between border-b border-ink/5 pb-2">
                  <span className="text-ink-soft">{k}</span>
                  <span className={cls || "text-ink"}>{v}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className={cardCls}>
            <p className="overline text-matcha-dark mb-4">By Payment</p>
            <Bars data={closing.by_payment} fmt={fmt} />
            <p className="overline text-matcha-dark mt-6 mb-4">By Channel</p>
            <Bars data={closing.by_channel} fmt={fmt} />
          </div>

          <div className={cardCls}>
            <p className="overline text-matcha-dark mb-4">By Hour</p>
            <Bars data={closing.by_hour} fmt={fmt} suffix=":00" />
            <p className="overline text-matcha-dark mt-6 mb-4">By Staff</p>
            <Bars data={closing.by_staff} fmt={fmt} />
          </div>
        </div>
      )}
    </MgmtLayout>
  );
}

function Bars({ data, fmt, suffix = "" }) {
  const entries = Object.entries(data || {});
  if (!entries.length) return <p className="text-ink-soft text-xs">No data.</p>;
  const max = Math.max(...entries.map(([, v]) => v));
  return (
    <ul className="space-y-2">
      {entries.map(([k, v]) => (
        <li key={k} className="text-xs">
          <div className="flex justify-between mb-1">
            <span className="text-ink">{k}{suffix}</span>
            <span className="text-ink-soft">{fmt ? fmt(v) : v}</span>
          </div>
          <div className="h-1.5 bg-ink/5 rounded-full overflow-hidden">
            <div className="h-full bg-matcha-dark" style={{ width: `${(v / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Customer Value Analysis — Average Order Value (AOV) / spend per customer.
 * Combines: today's AOV, month vs previous month, 30-day sparkline trend,
 * AOV by day-of-week, AOV by payment method, and top revenue-driving items.
 */
function CustomerValueCard({ aov }) {
  const money = (v) => `RM ${Number(v || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const changePct = aov.change_pct;
  const changePositive = typeof changePct === "number" && changePct >= 0;
  const dowMax = Math.max(1, ...aov.by_dow.map((d) => d.aov || 0));
  const trendMax = Math.max(1, ...aov.trend.map((t) => t.aov || 0));
  const trendMin = Math.min(...aov.trend.filter((t) => t.aov > 0).map((t) => t.aov), trendMax);

  // Build sparkline path
  const W = 400, H = 60, P = 4;
  const pts = aov.trend.map((t, i) => {
    const x = P + (i / Math.max(1, aov.trend.length - 1)) * (W - P * 2);
    const y = t.aov > 0
      ? P + (1 - (t.aov - trendMin) / Math.max(1, trendMax - trendMin)) * (H - P * 2)
      : null;
    return { ...t, x, y };
  });
  const linePath = pts
    .filter((p) => p.y !== null)
    .map((p, i, arr) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");
  const areaPath =
    linePath && `${linePath} L ${pts[pts.length - 1].x} ${H - P} L ${pts[0].x} ${H - P} Z`;

  return (
    <div className={cardCls + " mb-10"} data-testid="mgmt-aov-card">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <p className="overline text-matcha-dark">Customer Value Analysis</p>
          <p className="font-cormorant italic text-ink-soft text-sm mt-1">
            Average spend per transaction · {aov.month}
          </p>
        </div>
        <p className="text-[10px] text-ink-soft uppercase tracking-[0.22em]">
          POS-import days use real transaction counts
        </p>
      </div>

      {/* Top KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
        {/* Today AOV */}
        <div className="border-l-2 border-matcha-dark pl-4">
          <p className="overline text-ink-soft mb-1">Today · {aov.date.slice(5)}</p>
          <p className="font-display text-3xl text-ink" style={{ letterSpacing: "0.02em" }}>
            {money(aov.today.aov)}
          </p>
          <p className="text-[11px] text-ink-soft uppercase tracking-[0.18em] mt-1">
            {aov.today.orders} txns · {money(aov.today.revenue)}
          </p>
        </div>
        {/* This month AOV */}
        <div>
          <p className="overline text-ink-soft mb-1">This month</p>
          <p className="font-display text-3xl text-ink" style={{ letterSpacing: "0.02em" }}>
            {money(aov.this_month.aov)}
          </p>
          <p className="text-[11px] text-ink-soft uppercase tracking-[0.18em] mt-1">
            {aov.this_month.orders} txns · {money(aov.this_month.revenue)}
          </p>
        </div>
        {/* Prev month AOV + change */}
        <div>
          <p className="overline text-ink-soft mb-1">Last month · {aov.prev_month.slice(5)}</p>
          <p className="font-display text-3xl text-ink" style={{ letterSpacing: "0.02em" }}>
            {money(aov.prev_month_stats.aov)}
          </p>
          {typeof changePct === "number" ? (
            <p className={`text-[11px] uppercase tracking-[0.18em] mt-1 ${changePositive ? "text-matcha-dark" : "text-red-700"}`}>
              {changePositive ? "▲" : "▼"} {Math.abs(changePct).toFixed(1)}% vs last month
            </p>
          ) : (
            <p className="text-[11px] text-ink-soft uppercase tracking-[0.18em] mt-1">No baseline yet</p>
          )}
        </div>
        {/* 30-day sparkline */}
        <div>
          <p className="overline text-ink-soft mb-1">30-day trend</p>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="60" preserveAspectRatio="none" role="img" aria-label="AOV trend">
            {areaPath && <path d={areaPath} fill="#5A6B3E" fillOpacity="0.08" />}
            {linePath && (
              <path d={linePath} fill="none" stroke="#5A6B3E" strokeWidth="1.5" strokeDasharray="3 3" strokeLinecap="round" />
            )}
            {pts.map((p, i) => p.y !== null && (
              <circle key={i} cx={p.x} cy={p.y} r="1.8" fill="#2C3326">
                <title>{`${p.date} · AOV RM ${p.aov.toFixed(2)} · ${p.orders} txns`}</title>
              </circle>
            ))}
          </svg>
          <p className="text-[11px] text-ink-soft uppercase tracking-[0.18em] mt-1">
            {money(trendMin)} — {money(trendMax)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-ink/10">
        {/* AOV by day-of-week */}
        <div>
          <p className="overline text-ink mb-3">By day of week</p>
          <ul className="space-y-2" data-testid="mgmt-aov-by-dow">
            {aov.by_dow.map((d) => (
              <li key={d.dow} className="text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className={`${d.orders ? "text-ink" : "text-ink-soft"} w-10`}>{d.label}</span>
                  <span className={d.orders ? "text-ink font-mono" : "text-ink-soft font-mono"}>
                    {d.orders ? money(d.aov) : "—"}
                  </span>
                </div>
                <div className="h-1 bg-ink/5 rounded-full overflow-hidden">
                  <div className="h-full bg-matcha-dark/70" style={{ width: `${(d.aov / dowMax) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* AOV by payment method */}
        <div>
          <p className="overline text-ink mb-3">By payment method</p>
          {aov.by_payment.length === 0 ? (
            <p className="text-sm text-ink-soft italic">
              Upload the Payment Received report to see this.
            </p>
          ) : (
            <ul className="space-y-3" data-testid="mgmt-aov-by-payment">
              {aov.by_payment.map((p) => (
                <li key={p.method} className="border-b border-ink/5 pb-2">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-ink">{p.method}</span>
                    <span className="text-ink font-mono">{money(p.aov)}</span>
                  </div>
                  <p className="text-[11px] text-ink-soft uppercase tracking-[0.18em]">
                    {p.orders} txns · {money(p.revenue)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top revenue-driving items */}
        <div>
          <p className="overline text-ink mb-3">Top items · {aov.month}</p>
          {aov.top_items.length === 0 ? (
            <p className="text-sm text-ink-soft italic">
              No item-level data yet — upload the Store Itemized report.
            </p>
          ) : (
            <ol className="space-y-2" data-testid="mgmt-aov-top-items">
              {aov.top_items.map((t, i) => (
                <li key={i} className="text-sm border-b border-ink/5 pb-2">
                  <div className="flex items-center justify-between">
                    <span className="text-ink">
                      <span className="text-matcha-dark mr-2 font-mono">{String(i + 1).padStart(2, "0")}</span>
                      {t.name}
                    </span>
                    <span className="text-ink font-mono">{money(t.revenue)}</span>
                  </div>
                  <p className="text-[11px] text-ink-soft uppercase tracking-[0.18em] mt-0.5 ml-6">
                    {t.qty} sold · {money(t.revenue / t.qty)} / unit
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

// Small KPI card used by the monthly POS summary strip on the Dashboard.
// Visually matches the range strip inside Sales & Orders → Import from POS.
function PosStat({ label, value, sub, highlight, testid }) {
  return (
    <div
      className={`rounded-sm border p-4 ${highlight ? "bg-sage/15 border-matcha-dark/40" : "bg-bone border-ink/10"}`}
      data-testid={testid}
    >
      <p className="overline text-ink-soft mb-1">{label}</p>
      <p
        className={`font-display text-2xl leading-tight ${highlight ? "text-matcha-dark" : "text-ink"}`}
        style={{ letterSpacing: "0.02em" }}
      >
        {value}
      </p>
      {sub && <p className="text-[10px] text-ink-soft uppercase tracking-[0.18em] mt-1">{sub}</p>}
    </div>
  );
}

// -------------------------------------------------------------------
// PLSection — "clear system" view. Daily strip on top, Monthly stack
// in the middle, Payback Period card on the right. All numbers live.
// -------------------------------------------------------------------
function PLSection({ date, month, plDaily, plMonthly, onSaved }) {
  const [investment, setInvestment] = useState(plMonthly.outlet_investment || 0);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setInvestment(plMonthly.outlet_investment || 0); }, [plMonthly.outlet_investment]);

  const saveInvestment = async () => {
    setSaving(true);
    try {
      await api.put("/mgmt/settings", { outlet_investment: Number(investment) || 0 });
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  const money = (v) => `RM ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const opPositive = plMonthly.operating_profit >= 0;
  const payback = plMonthly.payback_period_months;

  return (
    <section className="mb-12" data-testid="mgmt-pl-section">
      <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
        <div>
          <p className="overline text-matcha-dark">The Clear System</p>
          <h2 className="font-display text-2xl text-ink" style={{ letterSpacing: "0.02em" }}>P&amp;L</h2>
        </div>
        {plMonthly.recipes_missing && (
          <p className="text-[11px] uppercase tracking-[0.18em] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-1">
            No product recipes wired · COGS will read 0 until you fill them in
          </p>
        )}
      </div>

      {/* DAILY strip — Revenue → Orders → AOV → Grab → Walk-in → Discounts */}
      <div className="mb-6">
        <p className="overline text-ink-soft mb-2">Daily · {date}</p>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="mgmt-pl-daily">
          <PLStat label="Revenue" value={money(plDaily.revenue)} highlight testid="pl-daily-revenue" />
          <PLStat label="Orders" value={String(plDaily.orders || 0)} testid="pl-daily-orders" />
          <PLStat label="AOV" value={money(plDaily.aov)} sub="per transaction" testid="pl-daily-aov" />
          <PLStat label="Grab" value={money(plDaily.grab)} sub={plDaily.revenue ? `${((plDaily.grab / plDaily.revenue) * 100).toFixed(0)}% of revenue` : "—"} testid="pl-daily-grab" />
          <PLStat label="Walk-in" value={money(plDaily.walk_in)} sub={plDaily.revenue ? `${((plDaily.walk_in / plDaily.revenue) * 100).toFixed(0)}% of revenue` : "—"} testid="pl-daily-walkin" />
          <PLStat label="Discounts" value={money(plDaily.discounts)} sub={plDaily.discounts > 0 ? "given today" : "none today"} testid="pl-daily-discounts" />
        </div>
      </div>

      {/* MONTHLY stack + Payback card, side by side on desktop. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-sm border border-ink/10 bg-bone p-6" data-testid="mgmt-pl-monthly">
          <p className="overline text-ink-soft mb-4">Monthly P&amp;L · {month}</p>
          <div className="space-y-2 font-mono text-sm">
            <PLRow label="Revenue" value={money(plMonthly.revenue)} testid="pl-mon-revenue" />
            <PLRow label="− COGS" value={money(plMonthly.cogs)} muted testid="pl-mon-cogs" />
            <PLRow label="= Gross Profit" value={money(plMonthly.gross_profit)} bold divider testid="pl-mon-gp" />
            <PLRow label="− Staff" value={money(plMonthly.staff_cost)} muted testid="pl-mon-staff" />
            <PLRow label="− Rent" value={money(plMonthly.rent)} muted testid="pl-mon-rent" />
            <PLRow label="− Utilities" value={money(plMonthly.utilities)} muted testid="pl-mon-utilities" />
            <PLRow label="− Marketing / Grab" value={money(plMonthly.marketing)} muted testid="pl-mon-marketing" />
            <CommissionSubRow channel="Grab"       label="↳ Grab commission"       data={plMonthly.commissions?.Grab} testid="pl-mon-commission-grab" />
            <CommissionSubRow channel="Foodpanda"  label="↳ Foodpanda commission"  data={plMonthly.commissions?.Foodpanda} testid="pl-mon-commission-foodpanda" />
            <PLRow label="− Other" value={money(plMonthly.other)} muted testid="pl-mon-other" />
            <CommissionSubRow channel="Card"       label="↳ Card MDR"              data={plMonthly.commissions?.Card} testid="pl-mon-commission-card" />
            <CommissionSubRow channel="TNG"        label="↳ TNG MDR"               data={plMonthly.commissions?.TNG} testid="pl-mon-commission-tng" />
            <PLRow
              label="= Operating Profit"
              value={money(plMonthly.operating_profit)}
              bold
              divider
              highlight={opPositive ? "matcha" : "red"}
              testid="pl-mon-op"
            />
          </div>
        </div>

        {/* Payback card */}
        <div className={`rounded-sm border p-6 ${payback !== null ? "border-matcha-dark/40 bg-sage/15" : "border-ink/10 bg-bone"}`} data-testid="mgmt-pl-payback">
          <p className="overline text-matcha-dark mb-2">Payback Period</p>
          <div className="mb-4">
            <label className="overline text-ink-soft block mb-1">Outlet Investment (RM)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="1000"
                data-testid="pl-outlet-investment"
                value={investment}
                onChange={(e) => setInvestment(e.target.value)}
                className="bg-bone border border-ink/20 rounded-sm px-3 py-2 text-sm w-full font-mono"
              />
              <button
                type="button"
                data-testid="pl-outlet-investment-save"
                onClick={saveInvestment}
                disabled={saving || Number(investment) === Number(plMonthly.outlet_investment)}
                className="text-[11px] uppercase tracking-[0.22em] px-3 py-2 rounded-full border border-ink text-ink hover:bg-ink hover:text-bone transition-colors disabled:opacity-40"
              >
                {saving ? "…" : "Save"}
              </button>
            </div>
          </div>
          <div className="pt-4 border-t border-ink/10">
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mb-1">
              {money(plMonthly.outlet_investment)} ÷ {money(plMonthly.operating_profit)}
            </p>
            <p
              className={`font-display text-4xl leading-tight ${payback !== null ? "text-matcha-dark" : "text-ink-soft"}`}
              style={{ letterSpacing: "0.02em" }}
              data-testid="pl-payback-value"
            >
              {payback !== null ? `${payback} mo` : "—"}
            </p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-soft mt-1">
              {payback === null && Number(plMonthly.outlet_investment) === 0 && "Enter your investment to compute"}
              {payback === null && Number(plMonthly.outlet_investment) > 0 && "Operating profit not positive yet"}
              {payback !== null && `${(payback / 12).toFixed(1)} year${payback / 12 === 1 ? "" : "s"} to recover`}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Row inside the Monthly P&L stack. `divider` adds a hairline separator
// above (marks the = Gross Profit / = Operating Profit lines).
function PLRow({ label, value, muted, bold, divider, highlight, testid }) {
  const valueColour =
    highlight === "matcha" ? "text-matcha-dark" :
    highlight === "red" ? "text-red-700" :
    "text-ink";
  return (
    <div
      className={`flex items-baseline justify-between gap-3 ${divider ? "pt-2 mt-1 border-t border-ink/10" : ""}`}
      data-testid={testid}
    >
      <span className={`${bold ? "text-ink" : muted ? "text-ink-soft" : "text-ink"} ${bold ? "font-medium" : ""}`}>
        {label}
      </span>
      <span className={`${valueColour} ${bold ? "font-medium text-base" : "text-sm"}`}>{value}</span>
    </div>
  );
}

// Sub-row shown under a PLRow (e.g. auto-computed commission breakdown from
// bank statement matching). Hovers to reveal the per-channel POS/bank/commission
// calculation so admins understand where the number came from.
function PLSubRow({ label, value, tip, testid }) {
  return (
    <div
      className="flex items-baseline justify-between gap-3 pl-4 -mt-1"
      title={tip || ""}
      data-testid={testid}
    >
      <span className="text-ink-soft/70 text-[11px] uppercase tracking-[0.12em]">{label}</span>
      <span className="text-ink-soft/70 text-[11px]">{value}</span>
    </div>
  );
}

// Per-channel commission sub-row. Always renders when the channel has ANY
// activity (POS or bank), so admins can see WHY the line is empty (e.g.
// bank imported but POS not yet). Renders nothing only when both sides are 0.
function CommissionSubRow({ channel, label, data, testid }) {
  if (!data) return null;
  const pos = Number(data.pos_gross || 0);
  const bank = Number(data.bank_net || 0);
  const commission = Number(data.commission || 0);
  const estimated = !!data.estimated;
  const rate = data.rate;
  const source = data.source || "";
  const merchantGross = Number(data.merchant_gross || 0);
  const merchantName = data.merchant_name || "";
  if (pos === 0 && bank === 0 && commission === 0) return null;
  const money = (v) => `RM ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const pct = (r) => `${(Number(r || 0) * 100).toFixed(2)}%`;
  let display;
  let hint = "";
  let dim = false;
  if (source === "merchant_stmt" && merchantGross > 0) {
    // Card MDR straight from the acquirer merchant statement. Show the
    // ACTUAL variance vs POS card revenue + the effective MDR% — the
    // same numbers the top-strip scorecard surfaces, so nothing new to
    // interpret. Fee itself is baked into the Operating Profit above.
    const variance = pos - merchantGross;
    const effective = merchantGross > 0 ? commission / merchantGross : 0;
    const varStr = `RM ${Number(variance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    display = `${varStr} variance · ${pct(effective)} MDR`;
    hint = `POS card ${money(pos)} − Statement gross ${money(merchantGross)} = ${varStr}. MDR fee ${money(commission)} = ${pct(effective)} of statement gross. Source: ${merchantName || "acquirer merchant statement"}.`;
  } else if (source === "contracted" && pos > 0) {
    // TNG uses the contracted 1% rate regardless of bank delta — the fixed
    // rate is authoritative for the P&L. Show fee + rate; the bank credit
    // is available in the tooltip for anyone who wants to reconcile.
    display = `${money(commission)} · ${pct(rate)} contracted`;
    hint = `Contracted rate ${pct(rate)} × ${money(pos)} POS = ${money(commission)}. Bank credited ${money(bank)} (used for reconciliation, not commission).`;
  } else if (pos > 0 && bank > 0) {
    // Actual = POS − Bank. Also compute effective %.
    const effective = pos > 0 ? commission / pos : 0;
    display = `${money(commission)} · ${pct(effective)} actual`;
    hint = `${money(pos)} POS − ${money(bank)} bank = ${money(commission)} (${pct(effective)} of POS)`;
  } else if (bank > 0 && pos === 0) {
    display = `— awaiting POS · bank ${money(bank)}`;
    hint = `Bank received ${money(bank)} but no ${channel} POS sales imported for this month yet.`;
    dim = true;
  } else if (pos > 0 && estimated) {
    // POS present, bank not settled yet — use contracted rate.
    display = `~${money(commission)} · est. ${pct(rate)}`;
    hint = `Estimated at contracted ${pct(rate)} rate: ${money(pos)} POS × ${pct(rate)} = ${money(commission)}. Import bank statement for actual.`;
    dim = true;
  } else if (pos > 0 && bank === 0) {
    display = `— awaiting bank · POS ${money(pos)}`;
    hint = `POS shows ${money(pos)} in ${channel} sales but no bank credit imported yet. Import the bank statement to compute commission.`;
    dim = true;
  }
  return (
    <div
      className={`flex items-baseline justify-between gap-3 pl-4 -mt-1 ${dim ? "opacity-70" : ""}`}
      title={hint}
      data-testid={testid}
    >
      <span className="text-ink-soft/70 text-[11px] uppercase tracking-[0.12em]">{label}</span>
      <span className="text-ink-soft/70 text-[11px]">{display}</span>
    </div>
  );
}

function PLStat({ label, value, sub, highlight, testid }) {
  return (
    <div
      className={`rounded-sm border p-3 ${highlight ? "bg-sage/15 border-matcha-dark/40" : "bg-bone border-ink/10"}`}
      data-testid={testid}
    >
      <p className="overline text-ink-soft mb-1">{label}</p>
      <p
        className={`font-display text-xl leading-tight ${highlight ? "text-matcha-dark" : "text-ink"}`}
        style={{ letterSpacing: "0.02em" }}
      >
        {value}
      </p>
      {sub && <p className="text-[10px] text-ink-soft uppercase tracking-[0.18em] mt-1">{sub}</p>}
    </div>
  );
}

